import React, { useState, useEffect } from 'react';
import { Card, Button, Table, Badge, Modal, Form, Row, Col, Toast, ToastContainer, Container, Alert } from 'react-bootstrap';
import { db, storage } from '../../firebase';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject, getStorage } from "firebase/storage";
import { doc } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { bannerHelpers, firestoreHelpers } from '../../utils/firestoreHelpers';
import {
  FiImage,
  FiCheckCircle,
  FiPauseCircle,
  FiPlus,
  FiEye,
  FiEdit,
  FiTrash2,
  FiUpload,
  FiX
} from 'react-icons/fi';

const BannerManagement = () => {
  const { userEmail, getUserBannersRef, getUserPath } = useAuth();
  const [banners, setBanners] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [editingBanner, setEditingBanner] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [alert, setAlert] = useState({ show: false, message: "", variant: "" });
  const [showBannerSuccessModal, setShowBannerSuccessModal] = useState(false);
  const [bannerSuccessTitle, setBannerSuccessTitle] = useState("");
  const [bannerSuccessMessage, setBannerSuccessMessage] = useState("");
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingBanner, setViewingBanner] = useState(null);

  // Ref to track if component is mounted
  const mountedRef = React.useRef(true);
  // Unique identifier for this component instance
  const componentId = React.useRef(`banner-mgmt-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);

  // Form state
  const [bannerForm, setBannerForm] = useState({
    bannerName: "",
    bannerImage: null,
    imagePreview: ""
  });

  // Ensure mounted ref is set correctly
  useEffect(() => {
    mountedRef.current = true;
    console.log("🔧 Component mounted, mountedRef set to true");
    return () => {
      mountedRef.current = false;
      console.log("🔧 Component unmounting, mountedRef set to false");
    };
  }, []);

  // Load banners from Firestore (user-specific)
  useEffect(() => {
    // Early return if no user email
    if (!userEmail) {
      console.log("⚠️ No user email, skipping banner listener setup");
      setBanners([]);
      setInitialLoading(false);
      return;
    }

    const bannersRef = getUserBannersRef();
    if (!bannersRef) {
      console.log("⚠️ No banners reference, skipping banner listener setup");
      setBanners([]);
      setInitialLoading(false);
      return;
    }

    console.log("🔗 Setting up Firestore listener for banners (Component ID:", componentId.current, ")");
    console.log("📍 Banners collection path:", bannersRef.path);

    // Use direct firestoreHelpers.subscribe instead of bannerHelpers.subscribeToBanners
    // to avoid the enhanced callback wrapper that might be causing issues
    console.log("🔗 Setting up direct Firestore subscription...");
    const unsubscribe = firestoreHelpers.subscribe(
      bannersRef,
      (bannersList) => {
        console.log("🔧 mountedRef.current:", mountedRef.current);

        // Temporarily remove mounted check to debug the issue
        console.log("🎯 INITIAL LISTENER TRIGGERED! Banner data received:", bannersList);
        console.log("📊 Banner data length:", bannersList.length);
        console.log("🔄 Setting banners state...");
        setBanners(bannersList);
        console.log("🔄 Setting initialLoading to false...");
        setInitialLoading(false);
        console.log("✅ Initial state updated with", bannersList.length, "banners");

        // Force a re-render check
        setTimeout(() => {
          console.log("🔍 State check after timeout - banners:", bannersList.length, "initialLoading should be false");
        }, 100);
      },
      (error) => {
        if (!mountedRef.current) {
          console.log("⚠️ Component unmounted, skipping error state update");
          return;
        }
        console.error("❌ Initial Firestore listener error:", error);
        setBanners([]);
      }
    );

    return () => {
      console.log("🧹 Cleaning up Firestore listener for banners (Component ID:", componentId.current, ")");
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [userEmail, getUserBannersRef]);

  // Show alert message
  const showAlert = (message, variant = "success") => {
    setAlert({ show: true, message, variant });
    setTimeout(() => setAlert({ show: false, message: "", variant: "" }), 3000);
  };



  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setBannerForm(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  // Handle image selection
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        showAlert("Please select a valid image file", "danger");
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        showAlert("Image size should be less than 5MB", "danger");
        return;
      }

      setBannerForm(prev => ({
        ...prev,
        bannerImage: file,
        imagePreview: URL.createObjectURL(file)
      }));
    }
  };

  // Reset form
  const resetForm = () => {
    // Clean up blob URL to prevent memory leaks
    if (bannerForm.imagePreview && bannerForm.imagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(bannerForm.imagePreview);
    }

    setBannerForm({
      bannerName: "",
      bannerImage: null,
      imagePreview: ""
    });
    setEditingBanner(null);
    setShowForm(false);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log("🎨 Banner form submission started");
    console.log("📋 Form data:", {
      bannerName: bannerForm.bannerName,
      hasImage: !!bannerForm.bannerImage,
      isEditing: !!editingBanner
    });

    if (!bannerForm.bannerName.trim()) {
      showAlert("Please enter banner name", "danger");
      return;
    }

    // Check for duplicate banner names (only for new banners or name changes)
    const bannerKey = bannerForm.bannerName.trim()
      .replace(/[^a-zA-Z0-9\s]/g, '') // Remove special characters
      .replace(/\s+/g, '_') // Replace spaces with underscores
      .toLowerCase(); // Convert to lowercase

    const existingBanner = banners.find(banner => banner.id === bannerKey);
    if (existingBanner && (!editingBanner || editingBanner.id !== bannerKey)) {
      showAlert("A banner with this name already exists. Please choose a different name.", "danger");
      return;
    }

    if (!editingBanner && !bannerForm.bannerImage) {
      showAlert("Please select a banner image", "danger");
      return;
    }

    setLoading(true);

    try {
      let imageUrl = editingBanner?.imageUrl || "";
      let imagePath = editingBanner?.imagePath || "";

      // Upload image if new image is selected
      if (bannerForm.bannerImage) {
        console.log("📁 Uploading image to Firebase Storage...");

        try {
          const storage = getStorage();
          const fileName = `${Date.now()}_${bannerForm.bannerImage.name}`;
          imagePath = `banners/${userEmail}/${fileName}`;
          const imageRef = storageRef(storage, imagePath);

          console.log("📤 Uploading to path:", imagePath);
          const snapshot = await uploadBytes(imageRef, bannerForm.bannerImage);
          console.log("✅ Upload successful, getting download URL...");

          imageUrl = await getDownloadURL(snapshot.ref);
          console.log("🔗 Download URL obtained:", imageUrl);

        } catch (uploadError) {
          console.error("❌ Image upload failed:", uploadError);
          showAlert(`Image upload failed: ${uploadError.message}`, "danger");
          setLoading(false);
          return;
        }
      }

      const bannerData = {
        bannerName: bannerForm.bannerName.trim(),
        imageUrl,
        imagePath,
        isActive: true, // Always set to active by default
        createdAt: editingBanner?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        // Additional metadata for file tracking
        fileName: bannerForm.bannerImage ? bannerForm.bannerImage.name : editingBanner?.fileName || "",
        fileSize: bannerForm.bannerImage ? bannerForm.bannerImage.size : editingBanner?.fileSize || 0,
        fileType: bannerForm.bannerImage ? bannerForm.bannerImage.type : editingBanner?.fileType || "",
        note: editingBanner?.note || ""
      };

      console.log("💾 Saving banner data:", bannerData);

      const bannersRef = getUserBannersRef();
      if (!bannersRef) {
        showAlert("User authentication required", "danger");
        return;
      }

      if (editingBanner && editingBanner.id !== bannerKey) {
        // If banner name changed, delete old entry and create new one
        console.log("✏️ Banner name changed, moving from:", editingBanner.id, "to:", bannerKey);
        const oldBannerRef = doc(bannersRef, editingBanner.id);
        await bannerHelpers.deleteBanner(oldBannerRef);
        await bannerHelpers.createBanner(bannersRef, bannerKey, bannerData);
        // Show centered success modal
        setBannerSuccessTitle('✅ Banner Updated');
        setBannerSuccessMessage('Banner updated successfully!');
        setShowBannerSuccessModal(true);
        setTimeout(() => setShowBannerSuccessModal(false), 3000);
        console.log("✅ Banner updated with new key:", bannerKey);
      } else if (editingBanner) {
        // Update existing banner with same name
        console.log("✏️ Updating banner with key:", bannerKey);
        const bannerRef = doc(bannersRef, bannerKey);
        await bannerHelpers.updateBanner(bannerRef, bannerData);
        // Show centered success modal
        setBannerSuccessTitle('✅ Banner Updated');
        setBannerSuccessMessage('Banner updated successfully!');
        setShowBannerSuccessModal(true);
        setTimeout(() => setShowBannerSuccessModal(false), 3000);
        console.log("✅ Banner updated successfully");
      } else {
        // Add new banner with name as key
        console.log("➕ Adding new banner with key:", bannerKey);
        console.log("📍 Saving to collection path:", bannersRef.path);
        console.log("💾 Banner data being saved:", JSON.stringify(bannerData, null, 2));
        await bannerHelpers.createBanner(bannersRef, bannerKey, bannerData);
        console.log("✅ Banner added with key:", bannerKey);
        console.log("🔄 Banner should now be visible in listener...");
        // Show centered success modal for creation
        setBannerSuccessTitle('✅ Banner Added');
        setBannerSuccessMessage('Banner added successfully!');
        setShowBannerSuccessModal(true);
        setTimeout(() => setShowBannerSuccessModal(false), 3000);
      }

      resetForm();
    } catch (error) {
      console.error("Error saving banner:", error);
      showAlert("Failed to save banner. Please try again.", "danger");
    } finally {
      setLoading(false);
    }
  };

  // Handle view banner
  const handleView = (banner) => {
    setViewingBanner(banner);
    setShowViewModal(true);
  };

  // Handle edit banner
  const handleEdit = (banner) => {
    setBannerForm({
      bannerName: banner.bannerName,
      bannerImage: null,
      imagePreview: banner.imageUrl
    });
    setEditingBanner(banner);
    setShowForm(true);
  };

  // Handle delete banner
  const handleDelete = async (banner) => {
    if (window.confirm(`Are you sure you want to delete "${banner.bannerName}"?`)) {
      try {
        setLoading(true);

        // Delete image from Firebase Storage if it exists
        if (banner.imagePath) {
          try {
            console.log("🗑️ Deleting image from storage:", banner.imagePath);
            const storage = getStorage();
            const imageRef = storageRef(storage, banner.imagePath);
            await deleteObject(imageRef);
            console.log("✅ Image deleted from storage successfully");
          } catch (deleteError) {
            console.warn("⚠️ Failed to delete image from storage:", deleteError);
            // Continue with banner deletion even if image deletion fails
          }
        }

        // Delete banner from Firestore
        const bannersRef = getUserBannersRef();
        if (!bannersRef) {
          showAlert("User authentication required", "danger");
          return;
        }
        const bannerRef = doc(bannersRef, banner.id);
        await bannerHelpers.deleteBanner(bannerRef);
        showAlert("Banner deleted successfully!", "success");
      } catch (error) {
        console.error("Error deleting banner:", error);
        showAlert("Failed to delete banner. Please try again.", "danger");
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Container fluid style={{ padding: '24px', background: '#f8f9fa', minHeight: '100vh' }}>
      {/* Alert */}
      {alert.show && (
        <Alert variant={alert.variant} className="mb-3">
          {alert.message}
        </Alert>
      )}

      {/* Clean Header */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="mb-1" style={{
                color: '#1e293b',
                fontWeight: '700',
                fontSize: '1.75rem'
              }}>
                <FiImage className="me-2" />
                Banner Management
              </h2>
              <p className="mb-0" style={{ color: '#64748b', fontSize: '0.95rem' }}>Manage app banners and promotional content</p>
            </div>
            <Button
              onClick={() => setShowForm(!showForm)}
              style={{
                background: showForm ? 'white' : '#667eea',
                border: showForm ? '1px solid #e5e7eb' : 'none',
                borderRadius: '10px',
                padding: '10px 20px',
                color: showForm ? '#64748b' : 'white',
                fontWeight: '600',
                fontSize: '0.9rem',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                if (showForm) {
                  e.target.style.borderColor = '#ef4444';
                  e.target.style.color = '#ef4444';
                } else {
                  e.target.style.background = '#5a67d8';
                }
              }}
              onMouseLeave={(e) => {
                if (showForm) {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.color = '#64748b';
                } else {
                  e.target.style.background = '#667eea';
                }
              }}
            >
              {showForm ? (
                <>
                  <FiX size={16} color="#ef4444" />
                  <span>Cancel</span>
                </>
              ) : (
                <>
                  <FiPlus size={16} />
                  <span>Add New Banner</span>
                </>
              )}
            </Button>
          </div>
        </Col>
      </Row>

      {/* Clean Header Section */}
      <Row className="mb-4">
        <Col>
          <Card style={{
            border: '1px solid #e5e7eb',
            borderRadius: '16px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
            background: 'white'
          }}>
            <Card.Body className="p-4">
              <Row className="align-items-center">
                <Col md={8}>
                  <div>
                    <div className="d-flex align-items-center mb-2">
                      <div style={{
                        width: '48px',
                        height: '48px',
                        background: '#667eea',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: '16px'
                      }}>
                        <FiImage size={24} color="white" />
                      </div>
                      <div>
                        <h3 className="mb-1 fw-bold" style={{ color: '#1e293b', fontSize: '1.5rem' }}>Banner Management</h3>
                        <p className="mb-0" style={{ color: '#64748b', fontSize: '0.9rem' }}>Manage your app banners and promotional content</p>
                      </div>
                    </div>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="text-center">
                    <div style={{
                      background: '#f8f9fa',
                      borderRadius: '12px',
                      padding: '20px',
                      border: '1px solid #e5e7eb'
                    }}>
                      <div style={{ fontSize: '2rem', marginBottom: '8px', color: '#667eea' }}>
                        <FiImage size={32} />
                      </div>
                      <h2 className="mb-1 fw-bold" style={{ color: '#1e293b' }}>{banners.length}</h2>
                      <p className="mb-0 small" style={{ color: '#64748b' }}>Total Banners</p>
                    </div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>


      {/* Banner Form */}
      {showForm && (
        <Row className="mb-4">
          <Col>
            <Card style={{
              border: '1px solid #e5e7eb',
              borderRadius: '16px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
            }}>
              <Card.Header style={{
                background: '#f8f9fa',
                borderBottom: '1px solid #e5e7eb',
                borderRadius: '16px 16px 0 0',
                padding: '16px 24px'
              }}>
                <h5 className="mb-0" style={{ color: '#1e293b', fontWeight: '600', fontSize: '1rem' }}>
                  {editingBanner ? (
                    <>
                      <FiEdit className="me-2" size={18} />
                      Edit Banner
                    </>
                  ) : (
                    <>
                      <FiPlus className="me-2" size={18} />
                      Add New Banner
                    </>
                  )}
                </h5>
              </Card.Header>
              <Card.Body>
                <Form onSubmit={handleSubmit}>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label style={{ fontWeight: 'bold', color: 'black' }}>Banner Name *</Form.Label>
                        <Form.Control
                          type="text"
                          name="bannerName"
                          value={bannerForm.bannerName}
                          onChange={handleInputChange}
                          placeholder="Enter banner name..."
                          required
                        />
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label style={{ fontWeight: 'bold', color: 'black' }}>Banner Image *</Form.Label>
                        <Form.Control
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          required={!editingBanner}
                        />
                        <Form.Text className="text-muted">
                          Supported formats: JPG, PNG, GIF. Max size: 5MB
                        </Form.Text>
                      </Form.Group>

                    </Col>

                    <Col md={6}>
                      {bannerForm.imagePreview && (
                        <div>
                          <Form.Label>Image Preview</Form.Label>
                          <div className="border rounded p-2">
                            <img
                              src={bannerForm.imagePreview}
                              alt="Banner preview"
                              style={{
                                width: '100%',
                                maxHeight: '200px',
                                objectFit: 'contain',
                                borderRadius: '4px'
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </Col>
                  </Row>

                  <div className="d-flex gap-2 mt-3">
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={loading}
                    >
                      {loading ? '⏳ Saving...' : (editingBanner ? '💾 Update Banner' : '➕ Add Banner')}
                    </Button>

                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Banners Table */}
      <Row>
        <Col>
          <Card style={{
            border: '1px solid #e5e7eb',
            borderRadius: '16px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
          }}>
            <Card.Header style={{
              background: '#f8f9fa',
              borderBottom: '1px solid #e5e7eb',
              borderRadius: '16px 16px 0 0',
              padding: '16px 24px'
            }} className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0" style={{ color: '#1e293b', fontWeight: '600', fontSize: '1rem' }}>📋 All Banners</h5>
              <small style={{ color: '#64748b' }}>
                {banners.length} banner{banners.length !== 1 ? 's' : ''} found
              </small>
            </Card.Header>
            <Card.Body className="p-0">
              {initialLoading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary mb-3" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <h5 className="text-muted">Loading banners...</h5>
                  <p className="text-muted">Please wait while we fetch your banners</p>
                </div>
              ) : banners.length === 0 ? (
                <div className="text-center py-5">
                  <div style={{ fontSize: '4rem', opacity: 0.3 }}>
                    <FiImage size={64} />
                  </div>
                  <h5 className="text-muted">No banners found</h5>
                  <p className="text-muted">Click "Add New Banner" to create your first banner</p>
                </div>
              ) : (
                <Table responsive hover className="mb-0">
                  <thead style={{ background: 'white' }}>
                    <tr>
                      <th style={{
                        padding: '16px 24px',
                        fontWeight: '700',
                        color: 'black',
                        border: 'none',
                        borderBottom: '1px solid #e5e7eb',
                        fontSize: '0.85rem',
                        textTransform: 'uppercase'
                      }}>Sr. No</th>
                      <th style={{
                        padding: '16px 24px',
                        fontWeight: '700',
                        color: '#black',
                        border: 'none',
                        borderBottom: '1px solid #e5e7eb',
                        fontSize: '0.85rem',
                        textTransform: 'uppercase'
                      }}>Banner Name</th>
                      <th style={{
                        padding: '16px 24px',
                        fontWeight: '700',
                        color: 'black',
                        border: 'none',
                        borderBottom: '1px solid #e5e7eb',
                        fontSize: '0.85rem',
                        textTransform: 'uppercase'
                      }}>Image</th>
                      <th style={{
                        padding: '16px 24px',
                        fontWeight: '700',
                        color: 'black',
                        border: 'none',
                        borderBottom: '1px solid #e5e7eb',
                        fontSize: '0.85rem',
                        textTransform: 'uppercase'
                      }}>Created</th>
                      <th style={{
                        padding: '16px 24px',
                        fontWeight: '700',
                        color: 'black',
                        border: 'none',
                        borderBottom: '1px solid #e5e7eb',
                        fontSize: '0.85rem',
                        textTransform: 'uppercase',
                        textAlign: 'center'
                      }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {banners.map((banner, index) => (
                      <tr key={banner.id} style={{
                        backgroundColor: 'white',
                        transition: 'all 0.2s ease'
                      }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f8f9fa';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'white';
                        }}>
                        <td style={{
                          padding: '16px 24px',
                          border: 'none',
                          borderBottom: '1px solid #e5e7eb'
                        }}>
                          <span style={{
                            background: '#667eea',
                            color: 'white',
                            padding: '4px 10px',
                            borderRadius: '6px',
                            fontSize: '0.8rem',
                            fontWeight: '600'
                          }}>{index + 1}</span>
                        </td>
                        <td style={{
                          padding: '16px 24px',
                          border: 'none',
                          borderBottom: '1px solid #e5e7eb'
                        }}>
                          <strong style={{ color: '#1e293b', fontSize: '0.9rem' }}>{banner.bannerName}</strong>
                        </td>
                        <td style={{
                          padding: '16px 24px',
                          border: 'none',
                          borderBottom: '1px solid #e5e7eb'
                        }}>
                          {banner.imageUrl && banner.imageUrl.startsWith('blob:') ? (
                            <div style={{
                              width: '80px',
                              height: '50px',
                              backgroundColor: '#f8f9fa',
                              border: '1px solid #dee2e6',
                              borderRadius: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.8rem',
                              color: '#6c757d'
                            }}>
                              📷 Preview
                            </div>
                          ) : banner.imageUrl ? (
                            <img
                              src={banner.imageUrl}
                              alt={banner.bannerName}
                              style={{
                                width: '80px',
                                height: '50px',
                                objectFit: 'cover',
                                borderRadius: '4px',
                                border: '1px solid #dee2e6'
                              }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : (
                            <div style={{
                              width: '80px',
                              height: '50px',
                              backgroundColor: '#f8f9fa',
                              border: '1px solid #dee2e6',
                              borderRadius: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.8rem',
                              color: '#6c757d'
                            }}>
                              No Image
                            </div>
                          )}
                          <div style={{
                            display: 'none',
                            width: '80px',
                            height: '50px',
                            backgroundColor: '#f8f9fa',
                            border: '1px solid #dee2e6',
                            borderRadius: '4px',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.8rem',
                            color: '#6c757d'
                          }}>
                            Error Loading
                          </div>
                        </td>
                        <td style={{
                          padding: '16px 24px',
                          border: 'none',
                          borderBottom: '1px solid #e5e7eb'
                        }}>
                          <small style={{ color: '#64748b', fontSize: '0.85rem' }}>
                            {(() => {
                              if (!banner.createdAt) return 'N/A';
                              const date = banner.createdAt.toDate ? banner.createdAt.toDate() : new Date(banner.createdAt);
                              if (isNaN(date.getTime())) return 'N/A';
                              const day = String(date.getDate()).padStart(2, '0');
                              const month = String(date.getMonth() + 1).padStart(2, '0');
                              const year = date.getFullYear();
                              return `${day}/${month}/${year}`;
                            })()}
                          </small>
                        </td>
                        <td style={{
                          padding: '16px 24px',
                          border: 'none',
                          borderBottom: '1px solid #e5e7eb',
                          textAlign: 'center'
                        }}>
                          <div className="d-flex gap-2 justify-content-center align-items-center">
                            <button
                              onClick={() => handleView(banner)}
                              title="View banner"
                              style={{
                                width: '48px',
                                height: '48px',
                                borderRadius: '50%',
                                border: 'none',
                                background: '#e3f2fd',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                              }}
                            >
                              <FiEye size={20} color="#2196f3" strokeWidth={2.5} />
                            </button>
                            <button
                              onClick={() => handleEdit(banner)}
                              title="Edit banner"
                              style={{
                                width: '48px',
                                height: '48px',
                                borderRadius: '50%',
                                border: 'none',
                                background: '#fff3e0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                              }}
                            >
                              <FiEdit size={20} color="#ff9800" strokeWidth={2.5} />
                            </button>
                            <button
                              onClick={() => handleDelete(banner)}
                              title="Delete banner"
                              style={{
                                width: '48px',
                                height: '48px',
                                borderRadius: '50%',
                                border: 'none',
                                background: '#ffebee',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                              }}
                            >
                              <FiTrash2 size={20} color="#f44336" strokeWidth={2.5} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* View Banner Modal */}
      <Modal show={showViewModal} onHide={() => setShowViewModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FiEye className="me-2" size={18} />
            View Banner Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {viewingBanner && (
            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <h6 className="text-muted mb-2">Banner Information</h6>
                  <Card className="border-0 bg-light">
                    <Card.Body>
                      <div className="mb-3">
                        <strong>📝 Banner Name:</strong>
                        <div className="mt-1">{viewingBanner.bannerName}</div>
                      </div>


                      <div className="mb-3">
                        <strong>📅 Created:</strong>
                        <div className="mt-1">
                          {viewingBanner.createdAt ?
                            new Date(viewingBanner.createdAt).toLocaleString('en-IN') :
                            viewingBanner.createdAt && viewingBanner.createdAt.toDate ?
                              viewingBanner.createdAt.toDate().toLocaleString('en-IN') :
                              'N/A'
                          }
                        </div>
                      </div>

                      <div className="mb-3">
                        <strong>🔄 Last Updated:</strong>
                        <div className="mt-1">
                          {(() => {
                            if (!viewingBanner.updatedAt) return 'N/A';
                            try {
                              // Handle Firestore Timestamp
                              if (viewingBanner.updatedAt.toDate && typeof viewingBanner.updatedAt.toDate === 'function') {
                                return viewingBanner.updatedAt.toDate().toLocaleString('en-IN');
                              }
                              // Handle ISO string or number
                              const date = new Date(viewingBanner.updatedAt);
                              if (!isNaN(date.getTime())) {
                                return date.toLocaleString('en-IN');
                              }
                              return 'N/A';
                            } catch (error) {
                              console.error('Error parsing updatedAt:', error);
                              return 'N/A';
                            }
                          })()}
                        </div>
                      </div>

                      {viewingBanner.fileName && (
                        <div className="mb-3">
                          <strong>📎 File Name:</strong>
                          <div className="mt-1">{viewingBanner.fileName}</div>
                        </div>
                      )}

                      {viewingBanner.fileSize && viewingBanner.fileSize > 0 && (
                        <div className="mb-3">
                          <strong>📏 File Size:</strong>
                          <div className="mt-1">{(viewingBanner.fileSize / 1024 / 1024).toFixed(2)} MB</div>
                        </div>
                      )}

                      {viewingBanner.fileType && (
                        <div className="mb-3">
                          <strong>🎨 File Type:</strong>
                          <div className="mt-1">{viewingBanner.fileType}</div>
                        </div>
                      )}

                      {viewingBanner.note && (
                        <div className="mb-0">
                          <strong>📝 Note:</strong>
                          <div className="mt-1 text-muted small">{viewingBanner.note}</div>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </div>
              </Col>

              <Col md={6}>
                <div className="mb-3">
                  <h6 className="text-muted mb-2">Banner Preview</h6>
                  <Card className="border-0 bg-light">
                    <Card.Body className="text-center">
                      {viewingBanner.imageUrl ? (
                        <div>
                          <img
                            src={viewingBanner.imageUrl}
                            alt={viewingBanner.bannerName}
                            style={{
                              width: '100%',
                              maxHeight: '300px',
                              objectFit: 'contain',
                              borderRadius: '8px',
                              border: '1px solid #dee2e6',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                            }}
                          />
                          <div className="mt-3">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => window.open(viewingBanner.imageUrl, '_blank')}
                            >
                              🔍 View Full Size
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-4">
                          <div style={{ fontSize: '3rem', opacity: 0.3 }}>🖼️</div>
                          <p className="text-muted">No image available</p>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </div>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowViewModal(false)}>
            ❌ Close
          </Button>
          {viewingBanner && (
            <>
              <Button
                variant="warning"
                onClick={() => {
                  setShowViewModal(false);
                  handleEdit(viewingBanner);
                }}
              >
                ✏️ Edit Banner
              </Button>
              {viewingBanner.imageUrl && (
                <Button
                  variant="primary"
                  onClick={() => window.open(viewingBanner.imageUrl, '_blank')}
                >
                  🔍 Open Full Image
                </Button>
              )}
            </>
          )}
        </Modal.Footer>
      </Modal>

      {/* Banner success modal (centered) */}
      <Modal
        show={showBannerSuccessModal}
        onHide={() => setShowBannerSuccessModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{bannerSuccessTitle || '✅ Success'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ marginBottom: 0 }}>{bannerSuccessMessage}</p>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default BannerManagement;
